import {
    parseStoredDate,
    serializeStoredDate,
    startOfDay,
    type StoredDateMode,
} from "crqlar-react-common/dist/utils/date.util";
import {DATE_RANGE_FROM_ACROSS_TABS_KEY} from "crqlar-react-common/dist/utils/localStorage/localStorageConstants";

/**
 * The day a sector's reservation tabs are looking at.
 *
 * Floor plan, calendar, daily list and arrivals share this one sector-scoped
 * key, so moving the date on any of them carries over when the user switches
 * tab. `mode: "today"` is stored instead of a concrete day, so a tab reopened
 * tomorrow lands on tomorrow rather than on a stale day.
 */
export interface SectorDateSelection {
    date: Date;
    mode: StoredDateMode;
}

export const sectorDateStorageKey = (sectorId: string): string => (
    `${DATE_RANGE_FROM_ACROSS_TABS_KEY}-${sectorId}`
);

export const sectorDateSelection = (
    date: Date,
    mode: StoredDateMode = "fixed",
): SectorDateSelection => ({date: startOfDay(date), mode});

export const readStoredDateSelection = (value: string | null): SectorDateSelection | undefined => {
    const date = parseStoredDate(value);
    if (!date || Number.isNaN(date.getTime())) return undefined;

    let mode: StoredDateMode = "fixed";
    if (value?.trimStart().startsWith("{")) {
        try {
            if ((JSON.parse(value) as {mode?: unknown}).mode === "today") {
                mode = "today";
            }
        } catch {
            return undefined;
        }
    }

    return {date: startOfDay(date), mode};
};

export const readSectorDate = (sectorId: string | undefined): SectorDateSelection | undefined => {
    if (!sectorId || typeof window === "undefined") return undefined;

    try {
        return readStoredDateSelection(window.localStorage.getItem(sectorDateStorageKey(sectorId)));
    } catch {
        return undefined;
    }
};

export const persistSectorDate = (
    sectorId: string | undefined,
    selection: SectorDateSelection,
): void => {
    if (!sectorId || typeof window === "undefined") return;

    try {
        window.localStorage.setItem(
            sectorDateStorageKey(sectorId),
            serializeStoredDate(selection.date, selection.mode),
        );
    } catch {
        // Persistence is optional; the tab still works from its in-memory state.
    }
};
